import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { loggedInGuard } from './core/guards/logged-in.guard';
import { PendingChangesGuard } from './core/guards/pending-changes.guard';
import { PermissionType } from './core/enum/permission.enum';
import { rootAuthGuard } from './core/guards/root-auth.guard';
import { doorwayAuthGuard } from './core/guards/doorway-auth.guard';

export const routes: Routes = [
  {
    path: 'invitation',
    loadComponent: () =>
      import('./features/org-and-users/invitation/invitation.component').then(
        (m) => m.InvitationComponent
      ),
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    children: [
      {
        path: 'select-organization',
        loadComponent: () =>
          import(
            './features/org-and-users/organization-selection/organization-selection.component'
          ).then((m) => m.OrganizationSelectionComponent),
      },
      {
        path: 'select-branch',
        loadComponent: () =>
          import(
            './features/org-and-users/branch-selection/branch-selection.component'
          ).then((m) => m.BranchSelectionComponent),
      },
      {
        path: 'organization-initialization/:orgId',
        loadComponent: () =>
          import(
            './features/org-and-users/organization-initialization/organization-initialization.component'
          ).then((m) => m.OrganizationInitializationComponent),
      },
      {
        path: '',
        loadComponent: () =>
          import('./core/components/main-layout/main-layout.component').then(
            (m) => m.MainLayoutComponent
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'dashboard',
          },
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./features/system/dashboard/dashboard.component').then(
                (m) => m.DashboardComponent
              ),
          },

          {
            path: 'patients',
            loadComponent: () =>
              import('./features/clinical/patients/patients.component').then(
                (m) => m.PatientsComponent
              ),
            data: {
              requiredPermissions: [PermissionType.VIEW_PATIENT],
            },
          },
          {
            path: 'patients/:patientId/record/:visitId',
            loadComponent: () =>
              import(
                './features/clinical/patients/components/patient-detailed-emr/patient-detailed-emr.component'
              ).then((m) => m.PatientDetailedEmrComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_RECORD],
            },
          },
          {
            path: 'patients/:patientId/record',
            loadComponent: () =>
              import(
                './features/clinical/patients/components/patient-detailed-emr/patient-detailed-emr.component'
              ).then((m) => m.PatientDetailedEmrComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_RECORD],
            },
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./features/org-and-users/users/users.component').then(
                (m) => m.UsersComponent
              ),
            data: {
              requiredPermissions: [PermissionType.VIEW_USER],
            },
          },
          {
            path: 'organizations',
            canActivate: [rootAuthGuard],
            loadComponent: () =>
              import(
                './features/org-and-users/organizations/organizations.component'
              ).then((m) => m.OrganizationsComponent),
          },
          {
            path: 'modules',
            canActivate: [doorwayAuthGuard],
            loadComponent: () =>
              import(
                './features/org-and-users/modules/modules.component'
              ).then((m) => m.ModulesComponent),
          },
          {
            path: 'leads',
            loadComponent: () =>
              import('./features/crm/leads/leads.component').then(
                (m) => m.LeadsComponent
              ),
            data: {
              requiredPermissions: [
                PermissionType.VIEW_LEAD,
                PermissionType.VIEW_OWN_LEAD,
              ],
            },
          },
          {
            path: 'leads/analytics',
            loadComponent: () =>
              import(
                './features/crm/leads/analytics/lead-analytics.component'
              ).then((m) => m.LeadAnalyticsComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_LEAD],
            },
          },
          {
            path: 'records',
            loadComponent: () =>
              import(
                './features/clinical/visits/patient-search/records.component'
              ).then((m) => m.RecordsComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_RECORD],
            },
          },
          {
            path: 'emr/:patientId',
            loadComponent: () =>
              import(
                './features/clinical/visits/editor/patient-emr.component'
              ).then((m) => m.PatientEmrComponent),
            canDeactivate: [PendingChangesGuard],
            data: {
              requiredPermissions: [PermissionType.VIEW_EMR],
            },
          },
          {
            path: 'appointments',
            loadComponent: () =>
              import(
                './features/clinical/appointments/appointments.component'
              ).then((m) => m.AppointmentsComponent),
            data: {
              requiredPermissions: [
                PermissionType.VIEW_APPOINTMENT,
                PermissionType.VIEW_OWN_APPOINTMENT,
              ],
            },
          },
          {
            path: 'visit-records',
            loadComponent: () =>
              import(
                './features/clinical/visits/list/visit-records.component'
              ).then((m) => m.VisitRecordsComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_RECORD],
            },
          },
          {
            path: 'logs',
            loadComponent: () =>
              import('./features/system/logs/logs.component').then(
                (m) => m.LogsComponent
              ),
            data: {
              requiredPermissions: [PermissionType.VIEW_AUDIT_LOG],
            },
          },
          {
            path: 'communication-logs',
            loadComponent: () =>
              import(
                './features/system/communication-logs/communication-logs.component'
              ).then((m) => m.CommunicationLogsComponent),
            data: {
              requiredPermissions: [PermissionType.READ_LOGS],
            },
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./features/system/settings/settings.component').then(
                (m) => m.SettingsComponent
              ),
            children: [
              {
                path: 'general',
                loadComponent: () =>
                  import(
                    './features/system/settings/general/general.component'
                  ).then((m) => m.GeneralSettingsComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [PermissionType.VIEW_GENERAL_SETTINGS],
                },
              },
              {
                path: 'appointments',
                loadComponent: () =>
                  import(
                    './features/system/settings/appointment/appointment.component'
                  ).then((m) => m.AppointmentSettingsComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [
                    PermissionType.VIEW_APPOINTMENT_SETTINGS,
                  ],
                },
              },
              {
                path: 'billing',
                loadComponent: () =>
                  import(
                    './features/system/settings/billing/billing.component'
                  ).then((m) => m.BillingSettingsComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [PermissionType.VIEW_BILLING_SETTINGS],
                },
              },
              {
                path: 'taxes',
                loadComponent: () =>
                  import(
                    './features/system/settings/taxes/taxes.component'
                  ).then((m) => m.TaxesSettingsComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [PermissionType.VIEW_TAX_SETTINGS],
                },
              },
              {
                path: 'communication',
                loadComponent: () =>
                  import(
                    './features/system/settings/communication/communication.component'
                  ).then((m) => m.CommunicationComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [
                    PermissionType.VIEW_COMMUNICATION_SETTINGS,
                  ],
                },
              },
              {
                path: 'security',
                loadComponent: () =>
                  import(
                    './features/system/settings/security/security.component'
                  ).then((m) => m.SecuritySettingsComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [PermissionType.VIEW_SECURITY_SETTINGS],
                },
              },
              {
                path: 'clinical',
                loadComponent: () =>
                  import(
                    './features/system/settings/clinical/clinical.component'
                  ).then((m) => m.ClinicalSettingsComponent),
                canDeactivate: [PendingChangesGuard],
                data: {
                  requiredPermissions: [PermissionType.VIEW_CLINICAL_SETTINGS],
                },
              },
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'general',
              },
            ],
          },
          {
            path: 'pharmacy-items',
            loadComponent: () =>
              import(
                './features/operations/pharmacy-items/pharmacy-items.component'
              ).then((m) => m.PharmacyItemsComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_PHARMACY],
            },
          },
          {
            path: 'pharmacy-dispense',
            loadComponent: () =>
              import(
                './features/operations/pharmacy-dispense/pharmacy-dispense.component'
              ).then((m) => m.PharmacyDispenseComponent),
            data: {
              requiredPermissions: [PermissionType.DISPENSE_PRESCRIPTION],
            },
          },
          {
            path: 'billing',
            loadComponent: () =>
              import(
                './features/operations/billing/components/invoice-list/invoice-list.component'
              ).then((m) => m.InvoiceListComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_INVOICE],
            },
          },
          {
            path: 'billing/visit/:visitId/items',
            loadComponent: () =>
              import(
                './features/operations/billing/components/visit-billing-items/visit-billing-items.component'
              ).then((m) => m.VisitBillingItemsComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_INVOICE],
            },
          },
          {
            path: 'billing/create',
            loadComponent: () =>
              import('./features/operations/billing/billing.component').then(
                (m) => m.BillingComponent
              ),
            data: {
              requiredPermissions: [PermissionType.CREATE_INVOICE],
            },
          },
          {
            path: 'services',
            loadComponent: () =>
              import(
                './features/operations/services/components/service-list/service-list.component'
              ).then((m) => m.ServiceListComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_SERVICE],
            },
          },
          {
            path: 'diagnoses',
            loadComponent: () =>
              import(
                './features/operations/diagnosis/components/diagnosis-list/diagnosis-list.component'
              ).then((m) => m.DiagnosisListComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_DIAGNOSIS],
            },
          },
          {
            path: 'service-fulfillment',
            loadComponent: () =>
              import(
                './features/operations/service-fulfillment/service-fulfillment.component'
              ).then((m) => m.ServiceFulfillmentComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_INVOICE],
            },
          },
          {
            path: 'data-import',
            loadComponent: () =>
              import(
                './features/operations/data-import/data-import.component'
              ).then((m) => m.DataImportComponent),
            data: {
              // We rely on component-level checks (entity-selection) but require user to be authenticated and part of org.
              requiredPermissions: [], 
            },
          },
          {
            path: 'analytics',
            loadComponent: () =>
              import('./features/system/analytics/analytics.component').then(
                (m) => m.AnalyticsComponent
              ),
            data: {
              requiredPermissions: [PermissionType.VIEW_ANALYTICS],
            },
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import(
                './features/system/notifications/notifications.component'
              ).then((m) => m.NotificationsComponent),
            data: {
              requiredPermissions: [], // Open for all logged in users
            },
          },
          {
            path: 'help/data-import',
            loadComponent: () =>
              import('./features/help/components/data-import-help/data-import-help.component').then(
                (m) => m.DataImportHelpComponent
              ),
            data: {
              requiredPermissions: [],
            },
          },
          {
            path: 'help',
            loadComponent: () =>
              import('./features/help/help.component').then(
                (m) => m.HelpComponent
              ),
            data: {
              requiredPermissions: [],
            },
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './features/system/file-manager/file-manager.component'
              ).then((m) => m.FileManagerComponent),
            data: {
              requiredPermissions: [PermissionType.VIEW_DOCUMENT],
            },
          },
        ],
      },
    ],
  },
  {
    path: 'auth-callback',
    loadComponent: () =>
      import('./features/auth/auth-callback/auth-callback.component').then(
        (m) => m.AuthCallbackComponent
      ),
  },
  {
    path: 'signin',
    loadComponent: () =>
      import('./features/auth/signin/signin.component').then(
        (m) => m.SigninComponent
      ),
  },
  {
    path: 'public-invoice/:id',
    loadComponent: () =>
      import('./features/public-invoice/public-invoice-view.component').then(
        (m) => m.PublicInvoiceViewComponent
      ),
  },
  {
    path: 'invitation',
    loadComponent: () =>
      import('./features/org-and-users/invitation/invitation.component').then(
        (m) => m.InvitationComponent
      ),
  },
  {
    path: 'unauthorized',
    loadComponent: () =>
      import('./features/auth/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent
      ),
  },
  {
    path: 'logged-out',
    canActivate: [loggedInGuard],
    loadComponent: () =>
      import('./features/auth/logged-out/logged-out.component').then(
        (m) => m.LoggedOutComponent
      ),
  },
];
